import type { FC, ReactNode } from 'react'

import { useMemo } from 'react'
import { ArrowUpRight } from 'lucide-react'
import { createSilkField } from '@/lib/dots/fields'
import { DotCanvas } from '@/components/dots/dot-canvas'
import { Container } from '@/components/generals/container'
import { ButtonLink } from '@/components/generals/button-link'
import { RevealText } from '@/components/generals/reveal-text'

export const HeroSection: FC = (): ReactNode => {
    const field = useMemo(() => createSilkField(3), [])

    return (
        <section id='top' className='relative isolate flex min-h-[100svh] flex-col overflow-hidden bg-white'>
            <div aria-hidden='true' className='absolute inset-0 -z-10'>
                <DotCanvas
                    field={field}
                    grid
                    interactive
                    centerX={0.62}
                    centerY={0.42}
                    mobileCenterX={0.52}
                    mobileCenterY={0.3}
                />
                <div className='pointer-events-none absolute inset-x-0 bottom-0 h-[42%] bg-linear-to-t from-white via-white/80 to-transparent' />
            </div>

            <Container className='flex flex-1 flex-col pt-24 pb-10 md:pt-32 md:pb-14'>
                <div className='flex items-start justify-between gap-6 font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground motion-safe:animate-in fade-in duration-1000 fill-mode-both delay-500'>
                    <p>
                        Independent software studio
                        <br />
                        Milano, since 2016
                    </p>

                    <p className='hidden items-center gap-2 text-right sm:flex'>
                        <span aria-hidden='true' className='size-1.5 bg-azure-500 motion-safe:animate-pulse' />
                        Booking projects for Q1 2027
                    </p>
                </div>

                <div className='mt-auto grid gap-8 pt-16 lg:grid-cols-12 lg:items-end lg:gap-10'>
                    <RevealText
                        as='h1'
                        immediate
                        lines={["Software", "in motion."]}
                        delay={200}
                        stagger={90}
                        className='text-[clamp(3.6rem,11vw,10.5rem)] font-medium leading-[0.88] tracking-[-0.06em] text-ink lg:col-span-8'
                    />

                    <div className='motion-safe:animate-in fade-in slide-in-from-bottom-4 duration-1000 fill-mode-both delay-700 lg:col-span-4 lg:pb-4'>
                        <p className='max-w-sm text-[17px] leading-relaxed text-ink/75'>
                            Onda is an independent studio that designs and engineers digital products, cloud platforms and data systems for companies that refuse to stand still.
                        </p>

                        <div className='mt-7 flex flex-wrap gap-3'>
                            <ButtonLink href='#contact'>
                                Start a project
                                <ArrowUpRight className='size-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5' />
                            </ButtonLink>

                            <ButtonLink href='#work' variant='secondary'>
                                See our work
                            </ButtonLink>
                        </div>
                    </div>
                </div>
            </Container>
        </section>
    )
}
